import { useEffect, useState } from 'react';
import { supabase } from '../../api/apiService';

const DEFAULT_RECIPE_CARD_IMAGE = `${process.env.PUBLIC_URL}/marmite.png`;

const getRecipeCardImageUrl = (image: string | null | undefined): string =>
  image || DEFAULT_RECIPE_CARD_IMAGE;

interface UseRecipeCardImageOptions {
  recipeId: string;
  image: string | null | undefined;
}

interface UseRecipeCardImageResult {
  currentImageUrl: string;
  isImageLoaded: boolean;
  handleImageLoad: () => void;
}

export const useRecipeCardImage = ({
  recipeId,
  image,
}: UseRecipeCardImageOptions): UseRecipeCardImageResult => {
  const propImageUrl = getRecipeCardImageUrl(image);
  const [currentImageUrl, setCurrentImageUrl] = useState(propImageUrl);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    if (currentImageUrl !== propImageUrl) {
      setCurrentImageUrl(propImageUrl);
      setIsImageLoaded(false);
    }
  }, [currentImageUrl, propImageUrl, recipeId]);

  useEffect(() => {
    if (image || !recipeId) {
      return;
    }

    const channel = supabase
      .channel(`recipe_image_update:${recipeId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'recipes', filter: `id=eq.${recipeId}` },
        (payload: { new: { id: string; image: string | null } }) => {
          const nextImageUrl = payload.new?.image;

          if (nextImageUrl && nextImageUrl !== currentImageUrl) {
            setCurrentImageUrl(nextImageUrl);
            setIsImageLoaded(false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentImageUrl, image, recipeId]);

  return {
    currentImageUrl,
    isImageLoaded,
    handleImageLoad: () => setIsImageLoaded(true),
  };
};

export default useRecipeCardImage;
